import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './CategorySelectDrawer.styles';
import { findSubcategoriesByName } from './CategorySelectBox.helpers';
import { Drawer } from '@/components/Drawer/Drawer';
import {
  AngleBackIcon,
  CategoriesIcon,
  CloseIcon,
} from '@/components/UI/Icons/Icons';
import { Category } from '@/api/types/categories.types';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import {
  SearchInput,
  SearchInputRef,
} from '@/components/SearchInput/SearchInput';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { useNewAdContext } from '@/contexts/NewAdContext';
import { NewAdPageTestIds } from '@/__e2e__/testIds/new-ad-page';

interface CategorySelectDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSubcategory: (subcategory: Category | null) => void;
}

export const CategorySelectDrawer: FC<CategorySelectDrawerProps> = ({
  isOpen,
  onClose,
  onSelectSubcategory,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const searchInputRef = useRef<SearchInputRef>(null);

  const {
    categories,
    selectedSubcategory,
    selectedCategory,
    setSelectedCategory,
  } = useNewAdContext();

  const [isSubcategoryOpen, setIsSubcategoryOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    searchInputRef.current?.clear();
  }, [isOpen]);

  const filteredSubcategories = useMemo(
    () =>
      findSubcategoriesByName(query, categories).flatMap(({ data }) => data),
    [query]
  );

  function openSubDrawer(category: Category) {
    setSelectedCategory(category);
    setIsSubcategoryOpen(true);
  }

  function closeSubDrawer() {
    setIsSubcategoryOpen(false);
  }

  function onCategoryClick(category: Category) {
    openSubDrawer(category);
  }

  function onSubcategoryClick(subcategory: Category) {
    onSelectSubcategory(subcategory);
    closeSubDrawer();
  }

  return (
    <Drawer
      isAdaptive
      dataTestId={NewAdPageTestIds.categorySelect.drawer}
      isOpen={isOpen}
      right={{
        title: (
          <CloseIcon
            width={15}
            height={15}
            fill={theme.palette.primary.labels}
          />
        ),
        onClick: onClose,
      }}
      title={t('pages.new_ad.new_ad')}
      hasBottomBorder={false}
      hasBottomMargin={false}
      background={theme.palette.primary.background}
      subtitle={
        <S.Container>
          <SearchInput
            onSearch={setQuery}
            ref={searchInputRef}
            hasPadding={false}
            hasBorderBottom={false}
            customPaddingTop={12}
            customPaddingBottom={12}
            placeholder={t('catalogue.category_search')}
            leftIcon={<CategoriesIcon fill={theme.palette.tertiary.labels} />}
          />
        </S.Container>
      }
    >
      {query && filteredSubcategories.length ? (
        <S.CategoryItems $gap={15}>
          {filteredSubcategories.map(subcategory => (
            <S.SubcategoryFilteredItem
              dataTestId={NewAdPageTestIds.categorySelect.subcategoryItem}
              onChangeHook={() => onSubcategoryClick(subcategory)}
              key={subcategory.id}
              value={selectedSubcategory?.id === subcategory.id}
              appearance="radio"
              size="sm"
              customContent={
                <S.SubcategoryCustomContent>
                  {subcategory.parent?.icon && (
                    <img
                      alt=""
                      src={subcategory.parent.icon}
                      width={40}
                      height={40}
                      loading="lazy"
                    />
                  )}
                  <S.SubcategoryCustomInfo>
                    <Typography.Body $variant="4">
                      {subcategory.name}
                    </Typography.Body>
                    <Typography.Caption $variant="2">
                      {subcategory.parent?.name}
                    </Typography.Caption>
                  </S.SubcategoryCustomInfo>
                </S.SubcategoryCustomContent>
              }
              hasSwitch={false}
              hasBorderBottom={false}
            />
          ))}
        </S.CategoryItems>
      ) : (
        <>
          <S.CategoryItems>
            {categories.map(item => (
              <S.CategoryItem
                key={item.id}
                data-testid={NewAdPageTestIds.categorySelect.categoryItem}
                onClick={() => onCategoryClick(item)}
                $isSelected={selectedSubcategory?.parent?.id === item.id}
              >
                <S.CategoryItemIconContainer>
                  <img
                    alt=""
                    src={item.icon}
                    width={40}
                    height={40}
                    loading="lazy"
                  />
                </S.CategoryItemIconContainer>
                <S.CategoryLabel>{item.name}</S.CategoryLabel>
                <S.CategoryChevronIcon />
              </S.CategoryItem>
            ))}
          </S.CategoryItems>

          <Drawer
            isAdaptive
            dataTestId={NewAdPageTestIds.categorySelect.subdrawer}
            isOpen={isSubcategoryOpen}
            title={selectedCategory?.name}
            left={{
              icon: <AngleBackIcon fill={theme.palette.primary.labels} />,
              onClick: closeSubDrawer,
            }}
            hasBottomBorder={false}
            hasBottomMargin={false}
            background={theme.palette.primary.background}
          >
            <S.CategoryItems $gap={0}>
              {selectedCategory?.children?.map(subcategory => (
                <Checkbox
                  dataTestId={NewAdPageTestIds.categorySelect.subcategoryItem}
                  onChangeHook={() => onSubcategoryClick(subcategory)}
                  key={subcategory.id}
                  value={subcategory.id === selectedSubcategory?.id}
                  label={subcategory.name}
                  appearance="radio"
                  size="lg"
                />
              ))}
            </S.CategoryItems>
          </Drawer>
        </>
      )}
    </Drawer>
  );
};

CategorySelectDrawer.displayName = 'CategorySelectDrawer';
